import { motion } from 'framer-motion'
import { Container, Section, Heading, Text, FeatureCard } from '.'

// Icon components
const ZapIcon = () => (
  <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z" />
  </svg>
)

const CodeIcon = () => (
  <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
  </svg>
)

const BellIcon = () => (
  <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
    />
  </svg>
)

const ClockIcon = () => (
  <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
    />
  </svg>
)

const MailIcon = () => (
  <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
    />
  </svg>
)

const DollarIcon = () => (
  <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
    />
  </svg>
)

const ChartIcon = () => (
  <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
    />
  </svg>
)

export function FeaturesSection() {
  const features = [
    {
      icon: <ZapIcon />,
      title: 'Error Tracking',
      description:
        'Capture and group errors automatically. See stack traces, occurrence counts, and context to fix issues fast.',
    },
    {
      icon: <CodeIcon />,
      title: 'Easy SDK Integration',
      description:
        'Add our lightweight SDK to capture errors, expose health checks, and collect metrics automatically. Available for JavaScript, Python, React, and Next.js.',
    },
    {
      icon: <BellIcon />,
      title: 'Smart Alerts',
      description:
        'Get email notifications when errors occur or apps go down. Customize cooldowns to avoid alert fatigue.',
    },
    {
      icon: <ClockIcon />,
      title: 'Uptime Monitoring',
      description:
        'SDK creates health endpoints automatically. We check every 1-60 minutes and alert when apps go down or recover.',
    },
    {
      icon: <ChartIcon />,
      title: 'Metrics Monitoring',
      description:
        'SDK exposes Prometheus-compatible metrics automatically. Track CPU, memory, request counts, and custom metrics with threshold alerts.',
    },
    {
      icon: <MailIcon />,
      title: 'Flexible Alerting',
      description:
        'Create alert profiles with email, Slack, or webhooks. Route different alerts to different channels and keep your whole team informed.',
    },
    {
      icon: <DollarIcon />,
      title: 'Transparent Pricing',
      description:
        'Clear, simple pricing with a free forever tier. No per-seat fees, no surprise charges. Pay for what you use, nothing more.',
    },
  ]

  return (
    <Section className="relative bg-slate-50">
      <Container className="relative">
        <div className="space-y-16">
          {/* Section header */}
          <div className="text-center space-y-4 max-w-3xl mx-auto">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary-100 border border-primary-300">
              <span className="text-sm text-primary-800 font-medium">Features</span>
            </div>

            <Heading as="h2" className="text-4xl md:text-5xl font-bold">
              <span className="text-slate-900">Everything you need to</span>
              <br />
              <span className="bg-gradient-to-r from-primary-600 to-accent-500 bg-clip-text text-transparent">
                monitor your apps
              </span>
            </Heading>

            <Text size="lg" className="text-slate-600">
              No enterprise complexity. No confusing features. Just the essentials to keep your apps
              running.
            </Text>
          </div>

          {/* Features grid */}
          <motion.div
            className="grid md:grid-cols-2 lg:grid-cols-3 gap-8"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-100px' }}
            variants={{
              hidden: {},
              visible: {
                transition: {
                  staggerChildren: 0.1,
                },
              },
            }}
          >
            {features.map((feature, index) => (
              <motion.div
                key={index}
                variants={{
                  hidden: { opacity: 0, y: 40 },
                  visible: {
                    opacity: 1,
                    y: 0,
                    transition: {
                      duration: 0.6,
                      ease: [0.21, 0.47, 0.32, 0.98],
                    },
                  },
                }}
              >
                <FeatureCard
                  icon={feature.icon}
                  title={feature.title}
                  description={feature.description}
                />
              </motion.div>
            ))}
          </motion.div>

          {/* Bottom CTA */}
          <div className="text-center pt-8">
            <Text className="text-slate-600">Start monitoring in under 5 minutes →</Text>
          </div>
        </div>
      </Container>
    </Section>
  )
}
